import { getDisabledProductCheckboxTooltipText } from '@features/dashboard/widgets/settings/utils';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Checkbox,
  Collapse,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconComponent } from '@shared/components/IconComponent';
import { Product } from '@shared/protos/product';
import { snakeToTitleCase } from '@utils/string';
import React, { useCallback, useMemo, useState } from 'react';

const icon = <IconComponent name="ri-checkbox-blank-line" />;
const checkedIcon = <IconComponent name="ri-checkbox-line" />;

const StyledList = styled(List)({
  boxSizing: 'content-box',
  padding: 0.5,
  overflow: 'auto',
  maxHeight: 600,
  marginTop: '10px !important',
  marginLeft: '0px !important',
  marginRight: '-4px !important',
  border: '1px solid #505050',
  boxShadow: '0px 2px 10px 2px rgba(0, 0, 0, 0.1)',
});

type Props = {
  allProductGroups: Record<string, Product[]>;
  allSelectedProducts: Product[];
  selectedUserProducts: Product[];
  maxProductsLimit: number;
  onProductClick: (product: Product) => void;
  getProductCount: (product: Product) => number;
  isSelectionDisabled: (product: Product) => boolean;
};

export const SearchGroupedProducts = ({
  allProductGroups,
  allSelectedProducts,
  selectedUserProducts,
  maxProductsLimit,
  onProductClick,
  getProductCount,
  isSelectionDisabled,
}: Props) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const filteredProductGroups = useMemo(() => {
    if (!searchInput) return allProductGroups;

    const filteredGroups: { [key: string]: Product[] } = {};
    Object.keys(allProductGroups).forEach(groupName => {
      const allMatchingProductsInGroup = allProductGroups[groupName].filter(
        product =>
          product.full_name.toLowerCase().includes(searchInput.toLowerCase()) || product.symbol.toLowerCase().includes(searchInput.toLowerCase())
      );

      if (allMatchingProductsInGroup.length) {
        filteredGroups[groupName] = allMatchingProductsInGroup;
      }
    });

    return filteredGroups;
  }, [searchInput, allProductGroups]);

  const onToggleGroupClick = useCallback((groupName: string) => {
    setExpandedGroups(prev => (prev.includes(groupName) ? prev.filter(group => group !== groupName) : [...prev, groupName]));
  }, []);

  const isProductSelected = useCallback(
    (product: Product) => {
      return allSelectedProducts.find(elem => elem.symbol === product.symbol) !== undefined;
    },
    [allSelectedProducts]
  );

  return (
    <>
      <TextField
        placeholder="Search products"
        size="small"
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon sx={{ fontSize: 15 }} />
            </InputAdornment>
          ),
        }}
        fullWidth
        sx={{
          '& .MuiInputBase-input': {
            fontSize: 11,
          },
        }}
      />

      {Object.keys(filteredProductGroups).length ? (
        <StyledList>
          {Object.keys(filteredProductGroups).map((groupName, index) => (
            <Stack key={groupName}>
              <ListItemButton
                onClick={() => onToggleGroupClick(groupName)}
                sx={{ backgroundColor: '#585858', padding: '5px', paddingTop: 0, paddingBottom: 0 }}
              >
                <ListItemText
                  primary={snakeToTitleCase(groupName)}
                  primaryTypographyProps={{
                    style: {
                      fontSize: 11,
                    },
                  }}
                />
                {expandedGroups.includes(groupName) ? <ExpandLess sx={{ fontSize: 17 }} /> : <ExpandMore sx={{ fontSize: 17 }} />}
              </ListItemButton>
              <Collapse in={expandedGroups.includes(groupName)} timeout="auto" unmountOnExit>
                <List
                  component="div"
                  disablePadding
                  sx={{ borderLeft: '1px solid #505050', borderRight: '1px solid #505050', marginLeft: 0.1, marginRight: 0.1 }}
                >
                  {filteredProductGroups[groupName].map((product, index) => (
                    <React.Fragment key={`${product.symbol}-${groupName}`}>
                      <ListItem key={product.symbol} disableGutters sx={{ padding: 0 }}>
                        <ListItemIcon>
                          {isSelectionDisabled(product) ? (
                            <Tooltip
                              title={getDisabledProductCheckboxTooltipText(
                                product,
                                selectedUserProducts.length,
                                getProductCount(product),
                                maxProductsLimit
                              )}
                              arrow
                            >
                              <span>
                                {' '}
                                <Checkbox
                                  size="small"
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  disabled={true}
                                  checked={isProductSelected(product)}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <Checkbox
                              size="small"
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={isProductSelected(product)}
                              onChange={() => onProductClick(product)}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText primary={<Typography fontSize={11}>{product.full_name}</Typography>} />
                        <Typography variant="caption" fontSize={11} sx={{ textAlign: 'end', marginRight: 1 }}>
                          {product.symbol.toLocaleUpperCase()}
                        </Typography>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Collapse>
              {index !== Object.keys(filteredProductGroups).length - 1 && <Divider />}
            </Stack>
          ))}
        </StyledList>
      ) : (
        <Typography textAlign="center" variant="body">
          No products found
        </Typography>
      )}
    </>
  );
};
